import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';

export interface SessionRow {
  readonly sessionId: string;
  readonly userName: string | null;
  readonly userCode: string | null;
  readonly ipAddress: string;
  readonly browser: string | null;
  readonly os: string | null;
  readonly loginAt: string;
  readonly lastActivityAt: string;
  readonly status: string;
}

export function SessionMonitorPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly SessionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [terminatingId, setTerminatingId] = useState<string | null>(null);
  const [terminateError, setTerminateError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setError(null);
    apiRequest<{ items: readonly SessionRow[] } | readonly SessionRow[]>('/security/sessions', { token })
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r) ? r : (r as { items: readonly SessionRow[] }).items;
        setRows(list);
      })
      .catch((e) => {
        if (alive) setError((e as Error).message);
      });
    return () => { alive = false; };
  }, [token, reloadTick]);

  const onTerminate = useCallback(
    async (sessionId: string) => {
      setTerminatingId(sessionId);
      setTerminateError(null);
      try {
        await apiRequest(`/security/sessions/${encodeURIComponent(sessionId)}`, {
          method: 'DELETE',
          token,
        });
        setReloadTick((n) => n + 1);
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : (e as Error).message;
        setTerminateError(msg);
      } finally {
        setTerminatingId(null);
      }
    },
    [token],
  );

  return (
    <main aria-label="session-monitor" data-testid="session-monitor-page">
      <h1>{t('admin.session.title')}</h1>
      <p>{t('admin.session.intro')}</p>
      <button
        type="button"
        aria-label="refresh"
        onClick={() => setReloadTick((n) => n + 1)}
      >
        {t('admin.session.refresh')}
      </button>
      {error ? <p role="alert">{error}</p> : null}
      {terminateError ? <p role="alert" data-testid="terminate-error">{terminateError}</p> : null}
      {!rows ? (
        <p>{t('admin.session.loading')}</p>
      ) : (
        <table aria-label="session-table">
          <thead>
            <tr>
              <th>{t('admin.session.col.user')}</th>
              <th>{t('admin.session.col.ip')}</th>
              <th>{t('admin.session.col.device')}</th>
              <th>{t('admin.session.col.started')}</th>
              <th>{t('admin.session.col.lastActivity')}</th>
              <th>{t('admin.session.col.status')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7}>{t('admin.session.empty')}</td></tr>
            ) : (
              rows.map((s) => (
                <tr key={s.sessionId} data-testid={`session-row-${s.sessionId}`}>
                  <td>{personLabel(s.userName, s.userCode) ?? '—'}</td>
                  <td>{s.ipAddress}</td>
                  <td>{[s.browser, s.os].filter(Boolean).join(' · ') || '—'}</td>
                  <td>{at(s.loginAt)}</td>
                  <td>{at(s.lastActivityAt)}</td>
                  <td>{s.status}</td>
                  <td>
                    <button
                      type="button"
                      aria-label={`terminate-${s.sessionId}`}
                      disabled={terminatingId === s.sessionId}
                      onClick={() => onTerminate(s.sessionId)}
                    >
                      {terminatingId === s.sessionId ? t('admin.session.terminating') : t('admin.session.terminate')}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </main>
  );
}
